// ============================================
// Filter Panel - Auction search filters
// ============================================

import { useState } from "react";
import { AuctionFilters, AuctionType, AuctionSort, DEFAULT_AUCTION_FILTERS } from "../../types";

interface FilterPanelProps {
  filters: AuctionFilters;
  onFiltersChange: (filters: AuctionFilters) => void;
  onSearch: () => void;
  loading?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

// Available TLDs for filtering
const AVAILABLE_TLDS = ["com", "net", "org", "io", "co", "ai", "app", "dev", "xyz", "info"];

// Sort options
const SORT_OPTIONS: { value: AuctionSort; label: string }[] = [
  { value: "EndTimeAsc", label: "ENDING SOON" },
  { value: "EndTimeDesc", label: "ENDING LATER" },
  { value: "HighBidAsc", label: "PRICE: LOW" },
  { value: "HighBidDesc", label: "PRICE: HIGH" },
  { value: "BidsDesc", label: "MOST BIDDERS" },
  { value: "DomainAsc", label: "DOMAIN A-Z" },
];

// Auction type options
const AUCTION_TYPES: { value: AuctionType; label: string }[] = [
  { value: "Dropped", label: "DROPPED" },
  { value: "PrivateSeller", label: "PRIVATE SELLER" },
  { value: "PreRelease", label: "PRE-RELEASE" },
];

/**
 * FilterPanel Component
 *
 * Comprehensive filter panel matching dropcatch.com experience
 */
export function FilterPanel({
  filters,
  onFiltersChange,
  onSearch,
  loading = false,
  collapsed = false,
  onToggleCollapse,
  className = "",
}: FilterPanelProps) {
  const [localFilters, setLocalFilters] = useState<AuctionFilters>(filters);

  // Update local filter state
  const updateFilter = <K extends keyof AuctionFilters>(
    key: K,
    value: AuctionFilters[K]
  ) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Toggle auction type
  const toggleAuctionType = (type: AuctionType) => {
    const current = localFilters.auctionTypes;
    const newTypes = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    updateFilter("auctionTypes", newTypes);
  };

  // Toggle TLD
  const toggleTld = (tld: string) => {
    const current = localFilters.tlds;
    const newTlds = current.includes(tld)
      ? current.filter((t) => t !== tld)
      : [...current, tld];
    updateFilter("tlds", newTlds);
  };

  // Apply filters and search
  const handleApply = () => {
    onFiltersChange(localFilters);
    onSearch();
  };

  // Reset to defaults
  const handleReset = () => {
    setLocalFilters(DEFAULT_AUCTION_FILTERS);
    onFiltersChange(DEFAULT_AUCTION_FILTERS);
  };

  if (collapsed) {
    return (
      <div className={`border-2 border-green-700 bg-black ${className}`}>
        <button
          onClick={onToggleCollapse}
          className="w-full px-4 py-2 text-left text-green-500 hover:bg-green-900/30 flex items-center justify-between"
        >
          <span className="text-sm tracking-wider">FILTERS</span>
          <span className="text-green-600">[+]</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`border-2 border-green-500 bg-black ${className}`}>
      {/* Header */}
      <div className="px-4 py-2 border-b border-green-700 flex items-center justify-between">
        <span className="text-sm text-green-400 tracking-wider font-bold">
          SEARCH FILTERS
        </span>
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="text-green-600 hover:text-green-400 text-sm"
          >
            [-]
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Auction Types */}
        <div>
          <label className="block text-xs text-green-600 mb-2 tracking-wider">
            AUCTION TYPE
          </label>
          <div className="flex flex-wrap gap-2">
            {AUCTION_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => toggleAuctionType(type.value)}
                className={`px-3 py-1 border text-xs tracking-wider transition-colors ${
                  localFilters.auctionTypes.includes(type.value)
                    ? "border-green-500 bg-green-500 text-black"
                    : "border-green-700 text-green-600 hover:border-green-500"
                }`}
              >
                {localFilters.auctionTypes.includes(type.value) ? "[x]" : "[ ]"}{" "}
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* TLD Selection */}
        <div>
          <label className="block text-xs text-green-600 mb-2 tracking-wider">
            TLD FILTER
          </label>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_TLDS.map((tld) => (
              <button
                key={tld}
                onClick={() => toggleTld(tld)}
                className={`px-2 py-1 border text-xs tracking-wider transition-colors ${
                  localFilters.tlds.includes(tld)
                    ? "border-green-500 bg-green-500 text-black"
                    : "border-green-700 text-green-600 hover:border-green-500"
                }`}
              >
                .{tld.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Filters Row */}
        <div className="flex flex-wrap gap-4">
          {/* Ending Today */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFilters.endingToday}
              onChange={(e) => updateFilter("endingToday", e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <span className="text-xs text-green-500 tracking-wider">ENDING TODAY</span>
          </label>

          {/* Has Bids */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFilters.hasBids === true}
              onChange={(e) =>
                updateFilter("hasBids", e.target.checked ? true : null)
              }
              className="w-4 h-4 accent-green-500"
            />
            <span className="text-xs text-green-500 tracking-wider">WITH BIDS</span>
          </label>

          {/* No Dashes */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFilters.noDashes}
              onChange={(e) => updateFilter("noDashes", e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <span className="text-xs text-green-500 tracking-wider">NO DASHES</span>
          </label>

          {/* No Numbers */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFilters.noNumbers}
              onChange={(e) => updateFilter("noNumbers", e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <span className="text-xs text-green-500 tracking-wider">NO NUMBERS</span>
          </label>
        </div>

        {/* Price Range */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs text-green-600 mb-1 tracking-wider">
              MIN PRICE
            </label>
            <input
              type="number"
              value={localFilters.minPrice ?? ""}
              onChange={(e) =>
                updateFilter(
                  "minPrice",
                  e.target.value ? Number(e.target.value) : null
                )
              }
              placeholder="$0"
              className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none placeholder-green-800 text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-green-600 mb-1 tracking-wider">
              MAX PRICE
            </label>
            <input
              type="number"
              value={localFilters.maxPrice ?? ""}
              onChange={(e) =>
                updateFilter(
                  "maxPrice",
                  e.target.value ? Number(e.target.value) : null
                )
              }
              placeholder="$10000"
              className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none placeholder-green-800 text-sm"
            />
          </div>
        </div>

        {/* Sort Order */}
        <div>
          <label className="block text-xs text-green-600 mb-1 tracking-wider">
            SORT BY
          </label>
          <select
            value={localFilters.sort}
            onChange={(e) => updateFilter("sort", e.target.value as AuctionSort)}
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2 border-t border-green-900">
          <button
            onClick={handleApply}
            disabled={loading}
            className="flex-1 py-2 bg-green-500 text-black font-bold text-sm tracking-wider hover:bg-green-400 disabled:opacity-50"
          >
            {loading ? "SEARCHING..." : "APPLY FILTERS"}
          </button>
          <button
            onClick={handleReset}
            disabled={loading}
            className="px-4 py-2 border border-green-700 text-green-500 text-sm tracking-wider hover:border-green-500 disabled:opacity-50"
          >
            RESET
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact filter bar for inline display
 */
export function FilterBar({
  filters,
  onFiltersChange: _onFiltersChange,
  className = "",
}: {
  filters: AuctionFilters;
  onFiltersChange: (filters: Partial<AuctionFilters>) => void;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {/* Active TLDs */}
      {filters.tlds.length > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-green-600">TLD:</span>
          {filters.tlds.map((tld) => (
            <span
              key={tld}
              className="px-2 py-0.5 bg-green-500/20 border border-green-500 text-green-400 text-xs"
            >
              .{tld}
            </span>
          ))}
        </div>
      )}

      {/* Active auction types */}
      {filters.auctionTypes.length > 0 && filters.auctionTypes.length < 3 && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-green-600">TYPE:</span>
          {filters.auctionTypes.map((type) => (
            <span
              key={type}
              className="px-2 py-0.5 bg-green-500/20 border border-green-500 text-green-400 text-xs"
            >
              {type}
            </span>
          ))}
        </div>
      )}

      {/* Quick filter badges */}
      {filters.noDashes && (
        <span className="px-2 py-0.5 bg-green-900/50 border border-green-700 text-green-500 text-xs">
          NO-DASH
        </span>
      )}
      {filters.noNumbers && (
        <span className="px-2 py-0.5 bg-green-900/50 border border-green-700 text-green-500 text-xs">
          NO-NUM
        </span>
      )}
      {filters.endingToday && (
        <span className="px-2 py-0.5 bg-yellow-900/50 border border-yellow-700 text-yellow-500 text-xs">
          TODAY
        </span>
      )}
      {filters.hasBids && (
        <span className="px-2 py-0.5 bg-green-900/50 border border-green-700 text-green-500 text-xs">
          HAS BIDS
        </span>
      )}

      {/* Price range */}
      {(filters.minPrice || filters.maxPrice) && (
        <span className="px-2 py-0.5 bg-green-900/50 border border-green-700 text-green-500 text-xs">
          ${filters.minPrice || 0} - ${filters.maxPrice || "MAX"}
        </span>
      )}
    </div>
  );
}

export default FilterPanel;
